import { formatMoney } from '@/domain/store-setup/money';
import { countryLocale } from '@/domain/store-setup/store-country';
import { listJoin } from '@/domain/store-setup/display-names';
import { isAssistantRtl } from '@/domain/assistant/assistant-direction';
import type { IGhTwoFormLabel } from '../gpthelp-host-contract';

/** Interpolation values of a card string. */
export type ICardTextParams = Record<string, string | number>;

/**
 * The part of vue-i18n 8 the card format uses; the app's `i18n` instance
 * satisfies it.
 */
export interface ICardTranslator {
  t(key: string, locale: string, values: ICardTextParams): unknown;
  tc(
    key: string,
    choice: number,
    locale: string,
    values: ICardTextParams
  ): string;
}

/**
 * Everything a card builder needs to write text in the admin locale. Built
 * once per call by `createCardFormat`.
 */
export interface ICardFormat {
  locale: string;
  t(key: string, params?: ICardTextParams): string;
  tc(key: string, n: number, params?: ICardTextParams): string;
  /** «$ 14.500»: the store currency in the store country's format. */
  money(value: number): string;
  /** «1.284», «2,5»: a count or quantity in the store country's format. */
  number(value: number): string;
  /**
   * «0,005», «2,5»: a stock quantity in the store country's format, with the
   * 3 decimals the stock pages keep (plan P5-1: «0,005 kg» is 5 g).
   */
  quantity(value: number): string;
  /** «12 %» for 0.12: whole percents, as the admin language writes them. */
  percent(value: number): string;
  /**
   * «lun 5/10»: short weekday, day and month. An instant is read in the branch
   * time zone; a calendar date «2026-10-05» (e.g. a business date) is that
   * day as written.
   */
  date(value: Date | string): string;
  /** «21:40» (24-hour) in the branch time zone. Instants only. */
  time(value: Date | string): string;
  /** «Bebidas e Infusiones»: a conjunction list in the locale. */
  list(items: string[]): string;
}

export interface ICardFormatInput {
  i18n: ICardTranslator;
  /** The admin UI locale (`$i18n.locale`). */
  locale: string;
  /** Store currency code, e.g. «ARS». */
  currency: string;
  /** Store country code, e.g. «AR». */
  country: string;
  /** Branch IANA time zone. */
  timeZone: string;
}

/** First strong isolate … pop directional isolate. */
const FSI = '\u2068';
const PDI = '\u2069';

/**
 * Horizontal whitespace runs to collapse; line breaks stay for the paragraph
 * rule, and no-break spaces stay (prices, French digits).
 */
const HORIZONTAL_WHITESPACE = /[^\S\n\u00a0\u202f]+/g;

/** A line break run with the single spaces around it. */
const LINE_BREAKS = / ?\n[ \n]*/g;

/**
 * U+0000 and unpaired UTF-16 surrogates. With the `u` flag a well-formed
 * pair (an emoji) is one code point outside the range, so only lone halves
 * match.
 */
const UNSTORABLE_CHARS = /\u0000|[\ud800-\udfff]/gu;

/** Inert stand-ins for the values while vue-i18n picks the plural form. */
const VALUE_TOKEN = /\ue000(\d+)\ue001/g;

/** A timezone-less calendar date, e.g. a business date «2026-10-05». */
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Locales whose short date drops the comma CLDR puts after the weekday, so
 * it reads like the copy rule (§10 «dom 28/9»).
 */
const WEEKDAY_WITHOUT_COMMA_LOCALES = ['es'];

function primarySubtag(locale: string): string {
  return locale.split('-')[0].toLowerCase();
}

/**
 * Collapses spaces and tabs, keeps a blank line as the paragraph break the
 * widget renders («a\n\nb»), and turns a single line break into a space.
 */
function normalizeWhitespace(text: string): string {
  return text
    .replace(HORIZONTAL_WHITESPACE, ' ')
    .replace(LINE_BREAKS, (breaks) =>
      breaks.split('\n').length > 2 ? '\n\n' : ' '
    )
    .trim();
}

/** Isolates (U+2068) the characters open without their closing U+2069. */
function unclosedIsolates(chars: string[]): number {
  let open = 0;
  for (const char of chars) {
    if (char === FSI) open += 1;
    else if (char === PDI && open > 0) open -= 1;
  }
  return open;
}

/**
 * `text` without the characters GPTHelp cannot store (U+0000, half an
 * emoji): the widget and the server drop a whole card that carries one,
 * e.g. a proposal whose request ends in an emoji the widget cut in two.
 */
export function storableText(text: string): string {
  return text.replace(UNSTORABLE_CHARS, '');
}

/**
 * Makes the text storable, normalizes whitespace and, when it is longer
 * than `max` characters (code points), keeps `max − 1` of them and appends
 * «…», so every card string fits its host contract cap. A cut inside a bidi
 * isolate keeps the «…» next to the cut and closes the isolate within the
 * cap.
 */
export function clampText(text: string, max: number): string {
  const normalized = normalizeWhitespace(storableText(text));
  const chars = Array.from(normalized);
  if (chars.length <= max) return normalized;
  let kept = chars.slice(0, max - 1);
  while (kept.length + 1 + unclosedIsolates(kept) > max) {
    kept = kept.slice(0, -1);
  }
  const head = kept.join('').trimEnd();
  return `${head}…${PDI.repeat(unclosedIsolates(kept))}`;
}

/**
 * For `ar` and `ur`, wraps every interpolated value in a first-strong
 * isolate, so «Empanada (carne)» or «Combo #2» keep their brackets and signs
 * inside a right-to-left sentence. Other locales get the values unchanged.
 */
export function bidiIsolateParams(
  locale: string,
  params: ICardTextParams
): ICardTextParams {
  if (!isAssistantRtl(locale)) return params;
  const isolated: ICardTextParams = {};
  for (const [name, value] of Object.entries(params)) {
    isolated[name] = `${FSI}${value}${PDI}`;
  }
  return isolated;
}

/**
 * `fmt.t(key, params)` (or `fmt.tc` with `count`) within `max` characters:
 * when it is too long, the value of `fit` (a product, category or file
 * name) is shortened instead of the sentence, so «Abrir {name} en el Menú»
 * keeps its words.
 */
export function fitText(
  fmt: ICardFormat,
  key: string,
  params: ICardTextParams,
  fit: string,
  max: number,
  count: number | null = null
): string {
  const render = (values: ICardTextParams) =>
    count === null ? fmt.t(key, values) : fmt.tc(key, count, values);
  const text = render(params);
  const overflow = Array.from(normalizeWhitespace(text)).length - max;
  const value = String(params[fit] ?? '');
  if (overflow <= 0 || !value) return clampText(text, max);
  const keep = Math.max(
    1,
    Array.from(normalizeWhitespace(value)).length - overflow
  );
  return clampText(render({ ...params, [fit]: clampText(value, keep) }), max);
}

/** Characters (code points) `text` keeps on a card before any cut: storable, whitespace normalized. */
export function cardTextLength(text: string): number {
  return Array.from(normalizeWhitespace(storableText(text))).length;
}

/** Locales that write consecutive sentences without a space («…。…。»). */
const UNSPACED_SENTENCE_LOCALES = ['zh', 'ja'];

/** What the locale writes between two sentences. */
function sentenceSeparator(locale: string): string {
  return UNSPACED_SENTENCE_LOCALES.includes(primarySubtag(locale)) ? '' : ' ';
}

/** Whole sentences as one paragraph, spaced the way the locale writes them. */
export function joinSentences(locale: string, sentences: string[]): string {
  return sentences
    .filter((sentence) => sentence !== '')
    .join(sentenceSeparator(locale));
}

/**
 * Characters a next sentence can take after `sentences` in a paragraph of
 * at most `max` (the space before it included): `fitSentences` keeps it
 * when it is that long or shorter. Zero or less: nothing more fits.
 */
export function sentenceRoom(
  locale: string,
  sentences: string[],
  max: number
): number {
  const paragraph = joinSentences(locale, sentences);
  if (!paragraph) return max;
  return max - cardTextLength(paragraph) - sentenceSeparator(locale).length;
}

/**
 * Sentences in priority order as one paragraph within `max` characters: the
 * first one always (cut only when it alone is longer), then each next one
 * while the paragraph still fits. The first that does not fit is left out
 * with every one after it; no sentence is ever cut.
 */
export function fitSentences(
  locale: string,
  sentences: string[],
  max: number
): string {
  const kept: string[] = [];
  for (const sentence of sentences) {
    const paragraph = joinSentences(locale, [...kept, sentence]);
    if (kept.length && cardTextLength(paragraph) > max) break;
    kept.push(sentence);
  }
  return clampText(joinSentences(locale, kept), max);
}

/**
 * A two-form label the widget renders with `n === 1 ? one : other`, then
 * replaces `{n}` with the count (host contract §3.3). The params override
 * vue-i18n's predefined `n`/`count`, so `other` keeps the literal `{n}`.
 */
export function hostPlural(fmt: ICardFormat, key: string): IGhTwoFormLabel {
  return {
    one: fmt.tc(key, 1),
    other: fmt.tc(key, 2, { n: '{n}', count: '{n}' }),
  };
}

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

/**
 * Noon UTC of a calendar date label: the same day in the UTC formatter, where
 * `new Date('2026-10-05')` (UTC midnight) would read as the 4th in any zone
 * west of UTC. Null for anything else.
 */
function calendarDateAtNoonUtc(value: Date | string): Date | null {
  if (typeof value !== 'string') return null;
  const match = CALENDAR_DATE.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12));
}

export function createCardFormat(input: ICardFormatInput): ICardFormat {
  const { i18n, locale, currency, country, timeZone } = input;
  // Latin digits in every locale, like the prices and the widget.
  const intlLocale = `${locale}-u-nu-latn`;
  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'numeric',
  };
  const instantDateFormat = new Intl.DateTimeFormat(intlLocale, {
    ...dateOptions,
    timeZone,
  });
  const calendarDateFormat = new Intl.DateTimeFormat(intlLocale, {
    ...dateOptions,
    timeZone: 'UTC',
  });
  // h23, not `hour12: false`: older WebViews resolve the latter to h24 for
  // h12-default locales (en, hi, ar…) and print «24:05».
  const timeFormat = new Intl.DateTimeFormat(intlLocale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone,
  });
  const dropWeekdayComma = WEEKDAY_WITHOUT_COMMA_LOCALES.includes(
    primarySubtag(locale)
  );
  // Counts read like the money next to them (the country's grouping and
  // decimal marks); percents follow the language's typography («12 %» in
  // Spanish, «12%» in English). Latin digits for both.
  const numberFormat = new Intl.NumberFormat(
    `${countryLocale(country)}-u-nu-latn`,
    { maximumFractionDigits: 2 }
  );
  // Stock quantities keep the 3 decimals the stock pages store.
  const quantityFormat = new Intl.NumberFormat(
    `${countryLocale(country)}-u-nu-latn`,
    { maximumFractionDigits: 3 }
  );
  const percentFormat = new Intl.NumberFormat(intlLocale, {
    style: 'percent',
    maximumFractionDigits: 0,
  });

  function formatDate(format: Intl.DateTimeFormat, date: Date): string {
    if (!dropWeekdayComma) return format.format(date);
    return format
      .formatToParts(date)
      .map((part, index, parts) =>
        part.type === 'literal' &&
        part.value === ', ' &&
        parts[index - 1]?.type === 'weekday'
          ? ' '
          : part.value
      )
      .join('');
  }

  return {
    locale,
    t(key, params = {}) {
      const text = i18n.t(key, locale, bidiIsolateParams(locale, params));
      return typeof text === 'string' ? text : key;
    },
    tc(key, n, params = {}) {
      const values = bidiIsolateParams(locale, { n, count: n, ...params });
      // vue-i18n 8 interpolates before it splits the plural forms on «|», so
      // a value holding «|» («Pizza | Grande») would pick a wrong form. Pick
      // the form with inert tokens, then put the values in.
      const names = Object.keys(values);
      const tokens: ICardTextParams = {};
      names.forEach((name, index) => {
        tokens[name] = `\ue000${index}\ue001`;
      });
      return i18n
        .tc(key, n, locale, tokens)
        .replace(VALUE_TOKEN, (_token, index: string) =>
          String(values[names[Number(index)]])
        );
    },
    money: (value) => formatMoney(value, currency, country),
    number: (value) => numberFormat.format(value),
    quantity: (value) => quantityFormat.format(value),
    percent: (value) => percentFormat.format(value),
    date(value) {
      const calendarDate = calendarDateAtNoonUtc(value);
      return calendarDate
        ? formatDate(calendarDateFormat, calendarDate)
        : formatDate(instantDateFormat, toDate(value));
    },
    time: (value) => timeFormat.format(toDate(value)),
    list: (items) => listJoin(items, locale),
  };
}
